import type { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";

import type { SubscriptionData } from "@/services/types";
import { formatCurrency } from "@/services/utils/currency";
import { getSubscriptionImage } from "@/services/utils/images";
import { getSubscriptionDisplayDetails } from "@/services/utils/subscriptions";

type SubscriptionProps = {
    subscription: SubscriptionData;
} & React.ComponentProps<typeof Card>;

function Subscription({ subscription }: SubscriptionProps) {
    const {
        id: idAsNumber,
        name,
        price_incl_tax,
        discount,
        price_with_discount,
    } = subscription;
    const id = String(idAsNumber);
    const details = getSubscriptionDisplayDetails(subscription);
    return (
        <div className="group hover:cursor-pointer">
            <RadioGroupItem value={id} id={id} className="peer sr-only" />
            <Label
                htmlFor={id}
                className="flex h-full min-h-[360px] flex-col items-center justify-between gap-2 rounded-md border-2 border-muted bg-popover px-2 py-4 text-center md:hover:bg-orange-100/50 lg:py-6 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-orange-100/50 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-orange-100/50"
            >
                <h4 className="text-base font-semibold leading-tight lg:text-lg">
                    {name}
                </h4>
                <figure className="flex aspect-square w-full max-w-32 justify-center overflow-hidden transition-transform transform sm:max-w-40">
                    <img
                        className="h-full w-full object-contain"
                        src={getSubscriptionImage(subscription)}
                        alt={`Imagen de la suscripción ${name}`}
                    />
                </figure>
                <div className="my-1 flex flex-col space-y-1">
                    <ul className="text-xs text-muted-foreground space-y-1">
                        <li className="flex space-x-1 items-center justify-center">
                            <span>
                                <strong className="font-sans">
                                    {details.people}
                                </strong>{" "}
                                personas
                            </span>
                        </li>
                        <li className="flex space-x-1 items-center text-center">
                            <span>
                                <strong className="font-sans">
                                    {details.quantity}
                                </strong>{" "}
                                variedades de {details.productKind} de temporada
                            </span>
                        </li>
                        <li className="flex space-x-1 items-center justify-center">
                            <span>
                                <strong className="font-sans">
                                    {details.weight}
                                </strong>{" "}
                                kgs de peso
                            </span>
                        </li>
                    </ul>
                </div>
                <Separator />
                <div className="text-center">
                    <div className="text-xs text-muted-foreground">
                        <strong className="text-sm font-semibold">
                            <span className="font-sans">{`${discount}%`}</span>
                        </strong>{" "}
                        dto primera caja
                    </div>
                    <h5 className="font-sans text-xl font-bold text-primary lg:text-2xl">
                        {formatCurrency(price_with_discount)}
                    </h5>
                    <div className="text-xs text-muted-foreground">
                        y luego{" "}
                        <strong className="text-sm font-semibold font-sans">
                            {formatCurrency(price_incl_tax)}
                        </strong>{" "}
                    </div>
                    <div className="text-xs text-muted-foreground">+ envío</div>
                </div>
            </Label>
        </div>
    );
}

export default Subscription;
